import { create } from 'zustand';
import { audio as audioStore } from '../storage/db';
import { useAppStore } from './useAppStore';

export type PlayerV2Status =
  | 'idle'
  | 'resolving'
  | 'fetching'
  | 'buffering'
  | 'playing'
  | 'paused'
  | 'ended'
  | 'error';

type FetchProgress = { loaded: number; total?: number };

export type NextProvider = (currentStoryId: string) => string | undefined;
export type PlayOptions = { next?: NextProvider };

// Tiny silent WAV. Played synchronously inside the user-gesture click
// handler to unlock the <audio> element on iOS Safari, so the real
// play() after the async fetch is allowed.
const SILENT_AUDIO =
  'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=';

function isPreFetch(s: PlayerV2Status): boolean {
  return s === 'idle' || s === 'resolving' || s === 'fetching';
}

function maybePrefetchNext(
  get: () => PlayerV2State,
  set: (patch: Partial<PlayerV2State>) => void
): void {
  const state = get();
  if (state.nextStoryId) return;
  const cur = state.storyId;
  const next = state._next;
  if (!cur || !next) return;
  const nextId = next(cur);
  if (!nextId || nextId === cur) return;
  set({ nextStoryId: nextId });
  void audioStore.getWithProgress(nextId).catch(() => { /* best-effort */ });
}

type PlayerV2State = {
  status: PlayerV2Status;
  storyId?: string;
  title?: string;
  positionSec: number;
  durationSec: number;
  fetchProgress?: FetchProgress;
  source?: 'local' | 'remote';
  error?: string;
  nextStoryId?: string;

  _el?: HTMLAudioElement;
  _objectUrl?: string;
  _abort?: AbortController;
  _generation: number;
  _next?: NextProvider;

  attachElement: (el: HTMLAudioElement) => void;
  play: (storyId: string, opts?: PlayOptions) => Promise<void>;
  pause: () => void;
  resume: () => Promise<void>;
  stop: () => void;
  seek: (sec: number) => void;
  skipNext: () => void;
};

function revokeUrl(url?: string) {
  if (!url) return;
  try {
    URL.revokeObjectURL(url);
  } catch {
    /* noop */
  }
}

export const usePlayerV2 = create<PlayerV2State>((set, get) => ({
  status: 'idle',
  positionSec: 0,
  durationSec: 0,
  _generation: 0,

  attachElement: (el) => {
    if (get()._el === el) return;
    set({ _el: el });

    // The silent unlock-prime fires real audio events. Guard each
    // handler against pre-fetch states so those events are noise.
    el.addEventListener('loadedmetadata', () => {
      if (isPreFetch(get().status)) return;
      set({ durationSec: el.duration || 0 });
    });
    el.addEventListener('timeupdate', () => {
      if (isPreFetch(get().status)) return;
      set({ positionSec: el.currentTime });
      if ('mediaSession' in navigator && el.duration && isFinite(el.duration)) {
        try {
          navigator.mediaSession.setPositionState?.({
            duration: el.duration,
            position: el.currentTime,
            playbackRate: el.playbackRate,
          });
        } catch {
          /* noop */
        }
      }
    });
    el.addEventListener('play', () => {
      if (isPreFetch(get().status)) return;
      set({ status: 'playing' });
      maybePrefetchNext(get, set);
    });
    el.addEventListener('pause', () => {
      const s = get().status;
      if (isPreFetch(s) || s === 'ended') return;
      set({ status: 'paused' });
    });
    el.addEventListener('ended', () => {
      if (isPreFetch(get().status)) return;
      const state = get();
      const cur = state.storyId;
      const next = state._next;
      const nextId = state.nextStoryId ?? (cur && next ? next(cur) : undefined);
      if (nextId && next) {
        void state.play(nextId, { next });
      } else {
        set({ status: 'ended' });
      }
    });
    el.addEventListener('waiting', () => {
      if (get().status === 'playing') set({ status: 'buffering' });
    });
    el.addEventListener('playing', () => {
      if (isPreFetch(get().status)) return;
      set({ status: 'playing' });
      maybePrefetchNext(get, set);
    });
    el.addEventListener('error', () => {
      if (isPreFetch(get().status)) return;
      set({ status: 'error', error: 'Playback error' });
    });
  },

  play: async (storyId, opts) => {
    const state = get();
    const el = state._el;
    if (!el) {
      set({ status: 'error', error: 'Audio element not ready' });
      return;
    }

    state._abort?.abort();
    revokeUrl(state._objectUrl);
    el.pause();

    // iOS Safari unlock: must call play() synchronously inside the
    // user gesture, before any await. Subsequent play() with the real
    // blob URL will then succeed even though it's after async work.
    el.src = SILENT_AUDIO;
    el.play().catch(() => { /* unlock attempt */ });

    const generation = state._generation + 1;
    const abort = new AbortController();
    const title = useAppStore.getState().stories[storyId]?.slug.title ?? 'Story';

    set({
      _generation: generation,
      _abort: abort,
      _objectUrl: undefined,
      _next: opts?.next,
      nextStoryId: undefined,
      status: 'resolving',
      storyId,
      title,
      positionSec: 0,
      durationSec: 0,
      fetchProgress: undefined,
      source: undefined,
      error: undefined,
    });

    let blob: Blob | undefined;
    try {
      blob = await audioStore.getWithProgress(storyId, {
        signal: abort.signal,
        onSource: (src) => {
          if (get()._generation !== generation) return;
          set({
            source: src,
            status: src === 'remote' ? 'fetching' : 'buffering',
          });
        },
        onProgress: (loaded, total) => {
          if (get()._generation !== generation) return;
          set({ fetchProgress: { loaded, total } });
        },
      });
    } catch (e) {
      if ((e as DOMException)?.name === 'AbortError') return;
      if (get()._generation !== generation) return;
      set({ status: 'error', error: (e as Error).message });
      return;
    }

    if (get()._generation !== generation) return;

    if (!blob) {
      set({ status: 'error', error: 'Audio not found' });
      return;
    }

    const url = URL.createObjectURL(blob);
    el.src = url;
    set({ _objectUrl: url, status: 'buffering', fetchProgress: undefined });

    if ('mediaSession' in navigator) {
      try {
        const appState = useAppStore.getState();
        const story = appState.stories[storyId];
        const universeName = story
          ? appState.universes[story.universeId]?.name
          : undefined;
        navigator.mediaSession.metadata = new MediaMetadata({
          title,
          artist: 'Bedtimer',
          album: universeName ?? 'Stories',
        });
        navigator.mediaSession.setActionHandler('play', () => void get().resume());
        navigator.mediaSession.setActionHandler('pause', () => get().pause());
        navigator.mediaSession.setActionHandler('seekbackward', (d) => {
          const e2 = get()._el;
          if (e2) e2.currentTime = Math.max(0, e2.currentTime - (d.seekOffset ?? 10));
        });
        navigator.mediaSession.setActionHandler('seekforward', (d) => {
          const e2 = get()._el;
          if (e2)
            e2.currentTime = Math.min(
              e2.duration || 0,
              e2.currentTime + (d.seekOffset ?? 10)
            );
        });
      } catch {
        /* noop */
      }
    }

    try {
      await el.play();
    } catch (e) {
      if (get()._generation !== generation) return;
      set({ status: 'error', error: (e as Error).message });
    }
  },

  pause: () => {
    const el = get()._el;
    if (el && !el.paused) el.pause();
  },

  resume: async () => {
    const el = get()._el;
    if (!el || !el.src) return;
    try {
      await el.play();
    } catch (e) {
      set({ status: 'error', error: (e as Error).message });
    }
  },

  stop: () => {
    const state = get();
    state._abort?.abort();
    // Set status to idle first so events fired by the src removal
    // below are ignored by our handlers (they bail in pre-fetch).
    set({
      _generation: state._generation + 1,
      _abort: undefined,
      _objectUrl: undefined,
      _next: undefined,
      nextStoryId: undefined,
      status: 'idle',
      storyId: undefined,
      title: undefined,
      positionSec: 0,
      durationSec: 0,
      fetchProgress: undefined,
      source: undefined,
      error: undefined,
    });
    const el = state._el;
    if (el) {
      el.pause();
      el.removeAttribute('src');
      el.load();
    }
    revokeUrl(state._objectUrl);
  },

  seek: (sec) => {
    const el = get()._el;
    if (el) el.currentTime = sec;
  },

  skipNext: () => {
    const state = get();
    const cur = state.storyId;
    const next = state._next;
    const nextId = state.nextStoryId ?? (cur && next ? next(cur) : undefined);
    if (nextId && next) void state.play(nextId, { next });
  },
}));
